import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  MobileStepper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  LocationOn,
  Info,
  Check,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import axios from 'axios';
import { PropertyType } from '../types';
import LocationPicker from '../components/Map/LocationPicker';

interface CreationLinkData {
  valid: boolean;
  creationLink?: {
    id: string;
    token: string;
    expiresAt: string;
    maxUsages: number;
    remainingUsages: string | number;
    agentName: string;
    agentEmail: string;
  };
  message?: string;
  expired?: boolean;
  limitExceeded?: boolean;
}

const DEFAULT_LAT = 33.8886;
const DEFAULT_LNG = 35.4955;

const PublicPropertyCreationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [validating, setValidating] = useState(true);
  const [linkValid, setLinkValid] = useState(false);
  const [linkData, setLinkData] = useState<CreationLinkData | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    city: '',
    country: 'Lebanon',
    postalCode: '',
    propertyType: 'APARTMENT',
    price: '',
    size: '',
    bedrooms: '',
    bathrooms: '',
    latitude: DEFAULT_LAT as number,
    longitude: DEFAULT_LNG as number,
    features: [] as string[],
    newFeature: '',
    images: [] as string[],
  });

  const steps = ['Link Info', 'Basic Info', 'Details', 'Location', 'Review'];

  const validateLink = useCallback(async () => {
    try {
      setValidating(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
      const response = await axios.get(`${API_URL}/property-creation-links/public/${token}/validate`);
      
      if (response.data.valid) {
        setLinkValid(true);
        setLinkData(response.data);
      } else {
        setError(response.data.message);
        setLinkData(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate link');
      setLinkData(err.response?.data);
    } finally {
      setValidating(false);
    }
  }, [token]);

  useEffect(() => {
    validateLink();
  }, [validateLink]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddFeature = () => {
    if (formData.newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, prev.newFeature.trim()],
        newFeature: '',
      }));
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleLocationChange = (lat: number, lng: number) => {
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
  };

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

      const propertyData = {
        ...formData,
        price: parseFloat(formData.price),
        size: formData.size ? parseFloat(formData.size) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        latitude: formData.latitude,
        longitude: formData.longitude,
      };

      await axios.post(`${API_URL}/property-creation-links/public/${token}/create`, propertyData);

      setSuccess(true);
      setActiveStep(5);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  /* ─── Loading ─── */
  if (validating) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f7fa' }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  /* ─── Invalid link ─── */
  if (!linkValid || !linkData?.valid) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f7fa', px: 2 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: 'error.main' }}>
            {linkData?.expired ? 'Link Expired' : linkData?.limitExceeded ? 'Link Already Used' : 'Invalid Link'}
          </Typography>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>
            {linkData?.message || error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {linkData?.limitExceeded
              ? 'This is a one-time link and has already been used. Please contact the agent to request a new one.'
              : 'Please contact the agent or request a new creation link to proceed.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  /* ─── Main page ─── */
  return (
    <Box sx={{ minHeight: '100vh', width: '100%', backgroundColor: '#f5f7fa', py: { xs: 0, sm: 3, md: 4 } }}>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1100 }, mx: 'auto', px: { xs: 0, sm: 2, md: 3 } }}>

        {/* Header */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 0, sm: 2 }, borderRadius: { xs: 0, sm: 3 }, background: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box component="img" src="/logo.jpg" alt="Stone Properties"
              sx={{ height: { xs: 44, sm: 52 }, width: 'auto', filter: 'invert(1)', objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.05rem', sm: '1.35rem', md: '1.55rem' } }}>
                Submit Property — {linkData?.creationLink?.agentName || 'Stone Properties'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.72rem', sm: '0.85rem' } }}>
                One-time link &nbsp;·&nbsp; Expires:{' '}
                {linkData?.creationLink?.expiresAt ? new Date(linkData.creationLink.expiresAt).toLocaleString() : ''}
                &nbsp;·&nbsp; {linkData?.creationLink?.agentEmail}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mx: { xs: 2, sm: 0 }, mb: 2, borderRadius: 2 }}>{error}</Alert>
        )}

        {/* Success */}
        {success ? (
          <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: { xs: 0, sm: 3 }, textAlign: 'center' }}>
            <Check sx={{ fontSize: { xs: 64, sm: 88 }, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>Property Submitted!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>Your property is now pending review.</Typography>
            <Typography variant="body2" color="text.secondary">
              The agent (<strong>{linkData?.creationLink?.agentEmail}</strong>) will review and approve it shortly.
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden', boxShadow: { xs: 'none', sm: '0 4px 20px rgba(0,0,0,0.08)' } }}>

            {/* Desktop stepper */}
            {!isMobile && (
              <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                  ))}
                </Stepper>
              </Box>
            )}

            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>

              {/* STEP 0 — Link Info */}
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Link Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Agent</Typography>
                        <Typography variant="body1" fontWeight={600}>{linkData?.creationLink?.agentName}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Remaining Uses</Typography>
                        <Typography variant="body1" fontWeight={600}>{linkData?.creationLink?.remainingUsages ?? '1'}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                  <Alert severity="info" icon={<Info />} sx={{ mt: 3, borderRadius: 2 }}>
                    This form creates a new property in <strong>pending status</strong>. An administrator will review it before approval.
                  </Alert>
                </Box>
              )}

              {/* STEP 1 — Basic Info */}
              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Basic Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Property Title *" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., Luxury 3BR Apartment in Beirut" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Describe the property..." multiline rows={4} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Property Type</InputLabel>
                        <Select name="propertyType" value={formData.propertyType} onChange={(e) => setFormData(prev => ({ ...prev, propertyType: e.target.value }))} label="Property Type">
                          {Object.values(PropertyType).map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Price (USD) *" name="price" type="number" value={formData.price} onChange={handleInputChange} placeholder="e.g., 150000" />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* STEP 2 — Details */}
              {activeStep === 2 && (
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Property Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <TextField fullWidth label="Size (m²)" name="size" type="number" value={formData.size} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField fullWidth label="Bedrooms" name="bedrooms" type="number" value={formData.bedrooms} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField fullWidth label="Bathrooms" name="bathrooms" type="number" value={formData.bathrooms} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Features</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField size="small" label="Add feature" value={formData.newFeature}
                          onChange={(e) => setFormData(prev => ({ ...prev, newFeature: e.target.value }))}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()} sx={{ flex: 1 }} />
                        <Button variant="outlined" onClick={handleAddFeature} sx={{ minWidth: 44, px: 1.5 }}><Add /></Button>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {formData.features.map((feature, index) => (
                          <Chip key={index} label={feature} onDelete={() => handleRemoveFeature(index)} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* STEP 3 — Location */}
              {activeStep === 3 && (
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" /> Location
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Street Address *" name="address" value={formData.address} onChange={handleInputChange} placeholder="e.g., Mar Elias Street, Building 12" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="City *" name="city" value={formData.city} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Country" name="country" value={formData.country} onChange={handleInputChange} />
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Search for a location or click anywhere on the map to place the pin.
                  </Typography>
                  <Box sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <LocationPicker
                      lat={formData.latitude}
                      lng={formData.longitude}
                      searchSuffix={formData.country || ''}
                      onLocationChange={handleLocationChange}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, px: 2, py: 1, borderRadius: 2, backgroundColor: 'action.hover', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Lat:</strong> {Number(formData.latitude).toFixed(6)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Lng:</strong> {Number(formData.longitude).toFixed(6)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* STEP 4 — Review */}
              {activeStep === 4 && (
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Review & Submit</Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Title', value: formData.title },
                      { label: 'Price', value: formData.price ? `$${formData.price}` : '—' },
                      { label: 'Type', value: formData.propertyType },
                      { label: 'Size', value: formData.size ? `${formData.size} m²` : '—' },
                      { label: 'Bedrooms / Bathrooms', value: `${formData.bedrooms || '—'} / ${formData.bathrooms || '—'}` },
                      { label: 'Location', value: [formData.address, formData.city, formData.country].filter(Boolean).join(', ') },
                      { label: 'Coordinates', value: `${Number(formData.latitude).toFixed(5)}, ${Number(formData.longitude).toFixed(5)}` },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                    {formData.features.length > 0 && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Features</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formData.features.map((f, i) => <Chip key={i} label={f} size="small" />)}
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                  <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                    Click <strong>Submit</strong> to send this property for review. It will be in pending status until approved.
                  </Alert>
                </Box>
              )}

              {/* Navigation — desktop */}
              {!isMobile ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
                  <Button onClick={handleBack} disabled={activeStep === 0 || loading} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600, minWidth: 100 }}>
                    Back
                  </Button>
                  {activeStep < steps.length - 1 ? (
                    <Button onClick={handleNext} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, minWidth: 100 }}>
                      Next
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} variant="contained" disabled={loading} color="success" sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}>
                      {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit Property'}
                    </Button>
                  )}
                </Box>
              ) : (
                /* Navigation — mobile dot stepper */
                <MobileStepper
                  variant="dots"
                  steps={steps.length}
                  position="static"
                  activeStep={activeStep}
                  sx={{ mt: 3, borderTop: '1px solid', borderColor: 'divider', background: 'transparent', px: 0 }}
                  nextButton={
                    activeStep < steps.length - 1 ? (
                      <Button size="small" onClick={handleNext} disabled={loading} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Next <KeyboardArrowRight />
                      </Button>
                    ) : (
                      <Button size="small" onClick={handleSubmit} disabled={loading} variant="contained" color="success" sx={{ textTransform: 'none', fontWeight: 600 }}>
                        {loading ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
                      </Button>
                    )
                  }
                  backButton={
                    <Button size="small" onClick={handleBack} disabled={activeStep === 0 || loading} sx={{ textTransform: 'none', fontWeight: 600 }}>
                      <KeyboardArrowLeft /> Back
                    </Button>
                  }
                />
              )}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default PublicPropertyCreationPage;
